import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, X, Handshake, Megaphone, MapPin, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ImageUploadOrUrl from '../../components/ImageUploadOrUrl';
import MapPinPicker from '../../components/MapPinPicker';

const TIERS = [
  { v: 'platinum', label: 'Platinum', color: 'bg-slate-700' },
  { v: 'gold', label: 'Gold', color: 'bg-amber-500' },
  { v: 'silver', label: 'Silver', color: 'bg-slate-400' },
  { v: 'bronze', label: 'Bronze', color: 'bg-amber-700' },
  { v: 'media', label: 'Media partner', color: 'bg-fuchsia-500' },
];
const DEFAULT_PLACEMENTS = [
  { value: 'home_hero', label: 'Home — Hero', size: '1600 × 500 px' },
  { value: 'home_banner', label: 'Home — Banner intermedio', size: '1200 × 300 px' },
  { value: 'feed', label: 'Feed socios', size: '900 × 300 px' },
  { value: 'sidebar', label: 'Barra lateral', size: '600 × 900 px' },
  { value: 'map_popup', label: 'Mapa — Pop-up', size: '600 × 300 px' },
  { value: 'map_pin', label: 'Mapa — Pin publicitario', size: 'Imagen 400 × 200 px + icono 128 × 128 PNG' },
];
const PIN_ICONS = [
  { v: 'generic', label: 'Genérico naranja' },
  { v: 'star', label: 'Estrella' },
  { v: 'gift', label: 'Regalo / Promo' },
  { v: 'flag', label: 'Bandera' },
  { v: 'sale', label: 'Oferta' },
  { v: 'building', label: 'Edificio' },
  { v: 'logo', label: 'Logo del partner' },
];

export default function AdminPartners() {
  const [tab, setTab] = useState('partners');
  const [partners, setPartners] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {type:'partner'|'ad', data}
  const [placementsMeta, setPlacementsMeta] = useState(DEFAULT_PLACEMENTS);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, meta] = await Promise.all([
        api.get('/admin/partners'),
        api.get('/admin/ads'),
        api.get('/ads/meta').catch(() => ({ data: null })),
      ]);
      setPartners(p.data); setAds(a.data);
      if (meta?.data?.placements) setPlacementsMeta(meta.data.placements.map((x) => ({ value: x.value, label: x.label, size: x.size })));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savePartner = async (data) => {
    try {
      if (data.partner_id) await api.patch(`/admin/partners/${data.partner_id}`, data);
      else await api.post('/admin/partners', data);
      toast.success('Partner guardado'); setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const saveAd = async (data) => {
    try {
      if (data.ad_id) await api.patch(`/admin/ads/${data.ad_id}`, data);
      else await api.post('/admin/ads', data);
      toast.success('Publicidad guardada'); setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const deletePartner = async (id) => {
    if (!window.confirm('¿Eliminar partner?')) return;
    await api.delete(`/admin/partners/${id}`); load();
  };
  const deleteAd = async (id) => {
    if (!window.confirm('¿Eliminar banner?')) return;
    await api.delete(`/admin/ads/${id}`); load();
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div className="space-y-5" data-testid="admin-partners">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Partners & Publicidad</p>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Patrocinadores y campañas</h1>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab('partners')} data-testid="partners-tab-partners" className={`rounded-full px-4 py-1.5 text-xs font-bold ${tab === 'partners' ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500'}`}>
          <Handshake className="mr-1 inline h-3.5 w-3.5" /> Partners
        </button>
        <button onClick={() => setTab('ads')} data-testid="partners-tab-ads" className={`rounded-full px-4 py-1.5 text-xs font-bold ${tab === 'ads' ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500'}`}>
          <Megaphone className="mr-1 inline h-3.5 w-3.5" /> Banners
        </button>
        <button
          onClick={() => setModal({ type: tab === 'partners' ? 'partner' : 'ad', data: {} })}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-orange-500 px-4 py-1.5 text-xs font-bold text-white"
          data-testid={`partners-new-${tab}`}
        >
          <Plus className="h-3.5 w-3.5" /> Nuevo
        </button>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-cnib-blue" /></div> : (
        <>
          {tab === 'partners' && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="partners-grid">
              {partners.length === 0 && <p className="col-span-full text-sm text-slate-500">Sin partners aún.</p>}
              {partners.map((p) => {
                const tier = TIERS.find((t) => t.v === p.tier) || TIERS[0];
                return (
                  <div key={p.partner_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`partner-${p.partner_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      {p.logo_url && <img src={p.logo_url} alt="" className="h-14 w-14 rounded-xl object-cover" />}
                      <span className={`rounded-full ${tier.color} px-2 py-0.5 text-[10px] font-bold uppercase text-white`}>{tier.label}</span>
                    </div>
                    <p className="mt-2 font-heading text-sm font-extrabold text-slate-900">{p.name}</p>
                    <p className="line-clamp-2 text-xs text-slate-500">{p.description}</p>
                    <div className="mt-3 flex justify-end gap-1">
                      <button onClick={() => setModal({ type: 'partner', data: p })} className="rounded-full bg-slate-100 p-1.5"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => deletePartner(p.partner_id)} className="rounded-full bg-red-50 p-1.5 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'ads' && (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="ads-grid">
              {ads.length === 0 && <p className="col-span-full text-sm text-slate-500">Sin campañas aún.</p>}
              {ads.map((ad) => (
                <div key={ad.ad_id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`ad-${ad.ad_id}`}>
                  {ad.image_url && <img src={ad.image_url} alt="" className="h-32 w-full object-cover" />}
                  <div className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(ad.placements || (ad.placement ? [ad.placement] : [])).map((pl) => (
                        <span key={pl} className="rounded-full bg-cnib-blue/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-cnib-blue">
                          {placementsMeta.find((p) => p.value === pl)?.label || pl}
                        </span>
                      ))}
                    </div>
                    <p className="mt-1 font-heading text-sm font-extrabold text-slate-900">{ad.title}</p>
                    {ad.lat != null && ad.lng != null && (
                      <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                        <MapPin className="h-3 w-3 text-violet-500" />
                        {ad.lat.toFixed(4)}, {ad.lng.toFixed(4)} {ad.pin_label ? `· ${ad.pin_label}` : ''}
                      </p>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`text-[10px] font-bold ${ad.active ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {ad.active ? '● Activa' : '● Pausada'}
                      </span>
                      <div className="flex gap-1">
                        <button onClick={() => setModal({ type: 'ad', data: ad })} className="rounded-full bg-slate-100 p-1.5"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteAd(ad.ad_id)} className="rounded-full bg-red-50 p-1.5 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {modal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setModal(null)}>
          <div className="w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-5" style={{ maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-heading text-lg font-extrabold">{modal.type === 'partner' ? 'Partner' : 'Banner publicitario'}</h3>
              <button onClick={() => setModal(null)} className="rounded-full p-1"><X className="h-5 w-5" /></button>
            </div>
            {modal.type === 'partner' ? (
              <form onSubmit={(e) => { e.preventDefault(); savePartner({ ...modal.data, featured: !!modal.data.featured, active: modal.data.active !== false }); }} className="mt-4 space-y-3">
                <input required placeholder="Nombre" value={modal.data.name || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, name: e.target.value } })} className={inputCls} />
                <select value={modal.data.tier || 'bronze'} onChange={(e) => setModal({ ...modal, data: { ...modal.data, tier: e.target.value } })} className={inputCls}>
                  {TIERS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
                <input placeholder="URL logo" value={modal.data.logo_url || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, logo_url: e.target.value } })} className={inputCls} />
                <input placeholder="Website" value={modal.data.website || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, website: e.target.value } })} className={inputCls} />
                <textarea rows={2} placeholder="Descripción" value={modal.data.description || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })} className={inputCls} />
                <button type="submit" className="w-full rounded-full bg-orange-500 py-2 text-sm font-bold text-white">Guardar</button>
              </form>
            ) : (
              <form onSubmit={(e) => {
                e.preventDefault();
                const d = modal.data;
                if ((d.placements || []).includes('map_pin') && (d.lat == null || d.lng == null)) {
                  toast.error('Debes marcar la ubicación en el mapa para el pin publicitario');
                  return;
                }
                if (!d.image_url) { toast.error('Agrega una imagen'); return; }
                saveAd({ ...d, active: d.active !== false, placements: d.placements || [] });
              }} className="mt-4 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Título de la campaña *</label>
                    <input required placeholder="Ej. Promo verano Mall Equipetrol" value={modal.data.title || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })} className={inputCls} data-testid="ad-title" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">URL de destino al hacer click</label>
                    <input placeholder="https://..." value={modal.data.link_url || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, link_url: e.target.value } })} className={inputCls} data-testid="ad-link" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Descripción corta (visible al abrir el pin)</label>
                    <textarea rows={2} value={modal.data.description || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })} className={inputCls} maxLength={600} />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Ubicaciones de la campaña (multi-selección) *</label>
                  <div className="grid gap-2 sm:grid-cols-2" data-testid="ad-placements">
                    {placementsMeta.map((pl) => {
                      const checked = (modal.data.placements || []).includes(pl.value);
                      return (
                        <label key={pl.value} className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 transition-colors ${checked ? 'border-cnib-blue bg-cnib-blue/5' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-testid={`ad-placement-${pl.value}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const current = new Set(modal.data.placements || []);
                              if (e.target.checked) current.add(pl.value); else current.delete(pl.value);
                              setModal({ ...modal, data: { ...modal.data, placements: Array.from(current) } });
                            }}
                            className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-cnib-blue focus:ring-cnib-blue"
                          />
                          <div className="min-w-0">
                            <p className="flex items-center gap-1 text-xs font-bold text-slate-900">
                              {pl.label}
                              {checked && <Check className="h-3 w-3 text-cnib-blue" />}
                            </p>
                            <p className="text-[11px] text-slate-500">{pl.size}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Creativo principal * {(modal.data.placements || []).length > 0 ? `(se usará para todas las ubicaciones)` : ''}
                  </label>
                  <ImageUploadOrUrl
                    value={modal.data.image_url}
                    onChange={(url) => setModal({ ...modal, data: { ...modal.data, image_url: url } })}
                    sizeHint={placementsMeta.find((p) => (modal.data.placements || []).includes(p.value))?.size || '900 × 300 px'}
                    testid="ad-image"
                  />
                </div>

                {(modal.data.placements || []).includes('map_pin') && (
                  <div className="rounded-2xl border border-violet-200 bg-violet-50/50 p-4" data-testid="ad-map-section">
                    <p className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-violet-700">
                      <MapPin className="h-3.5 w-3.5" /> Pin publicitario
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-[11px] font-bold text-slate-600">Etiqueta corta (opcional)</label>
                        <input placeholder="Ej. Promo" maxLength={40} value={modal.data.pin_label || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, pin_label: e.target.value } })} className={inputCls} data-testid="ad-pin-label" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-bold text-slate-600">Icono del pin</label>
                        <select value={modal.data.pin_icon || 'generic'} onChange={(e) => setModal({ ...modal, data: { ...modal.data, pin_icon: e.target.value } })} className={inputCls} data-testid="ad-pin-icon">
                          {PIN_ICONS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                        </select>
                      </div>
                      {modal.data.pin_icon === 'logo' && (
                        <div className="sm:col-span-2">
                          <label className="mb-1 block text-[11px] font-bold text-slate-600">Icono PNG del pin (128 × 128 recomendado)</label>
                          <ImageUploadOrUrl
                            value={modal.data.pin_icon_url}
                            onChange={(url) => setModal({ ...modal, data: { ...modal.data, pin_icon_url: url } })}
                            sizeHint="128 × 128 PNG transparente"
                            testid="ad-pin-icon-upload"
                          />
                        </div>
                      )}
                      <div className="sm:col-span-2">
                        <MapPinPicker
                          value={modal.data.lat != null && modal.data.lng != null ? { lat: modal.data.lat, lng: modal.data.lng } : null}
                          onChange={(ll) => setModal({ ...modal, data: { ...modal.data, lat: ll.lat, lng: ll.lng } })}
                          color="#8B5CF6"
                          icon="dot"
                          testid="ad-map-picker"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold text-slate-500">Inicio (opcional)</label>
                    <input type="datetime-local" value={modal.data.starts_at?.slice(0, 16) || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, starts_at: e.target.value } })} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold text-slate-500">Fin (opcional)</label>
                    <input type="datetime-local" value={modal.data.ends_at?.slice(0, 16) || ''} onChange={(e) => setModal({ ...modal, data: { ...modal.data, ends_at: e.target.value } })} className={inputCls} />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={modal.data.active !== false} onChange={(e) => setModal({ ...modal, data: { ...modal.data, active: e.target.checked } })} className="h-4 w-4 rounded border-slate-300 text-cnib-blue" />
                  Campaña activa
                </label>

                <button type="submit" className="w-full rounded-full bg-orange-500 py-2.5 text-sm font-bold text-white hover:bg-orange-600" data-testid="ad-save">
                  Guardar campaña
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
